
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { COURSES } from '../data/courses';
import { Button, Card, ProgressBar, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { generateCertificateHtml } from '../utils/certificate';

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshProfile } = useAuth();
  
  const course = COURSES.find(c => c.id === courseId);
  const [currentModuleIdx, setCurrentModuleIdx] = useState(1);
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const [mode, setMode] = useState<'learning' | 'quiz'>('learning');
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizResult, setQuizResult] = useState<{passed: boolean, score: number, incorrectIndices: number[]} | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showRotatePrompt, setShowRotatePrompt] = useState(true);

  // Auto-hide rotate prompt after 4 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowRotatePrompt(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Load saved progress state
  useEffect(() => {
    // Only run this logic once to set the initial position. 
    // Subsequent updates to userProfile shouldn't override local navigation state (e.g. restart).
    if (userProfile && courseId && course && !initialLoadDone) {
       const progress = userProfile.progress[courseId];
       
       if (progress) {
         // --- EXPIRATION LOGIC ---
         const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
         const completionDate = progress.dateCompleted || (progress.quizPassed ? progress.lastAccessed : null);
         const isExpired = completionDate && (new Date().getTime() - new Date(completionDate).getTime() > ONE_YEAR_MS);
         
         if (isExpired) {
             setCurrentModuleIdx(1);
             setMode('learning');
             setInitialLoadDone(true);
             return; 
         }
         // ------------------------

         const maxCompleted = Math.max(0, ...progress.completedModules);
         if (maxCompleted < course.totalModules) {
           setCurrentModuleIdx(maxCompleted + 1);
         } else if (!progress.quizPassed && progress.quizScore === undefined) {
           // If they haven't passed AND haven't taken the quiz yet (score undefined) -> Go to quiz
           setMode('quiz');
         } else if (!progress.quizPassed && progress.quizScore !== undefined) {
            // They failed previously. We default to the quiz screen so they can try again.
            setMode('quiz');
         } else {
           // Compliant
           setCurrentModuleIdx(1);
         }
       }
       setInitialLoadDone(true);
    } else if (userProfile && !initialLoadDone) {
        // No progress found, mark as loaded
        setInitialLoadDone(true);
    }
  }, [userProfile, courseId, course, initialLoadDone]);

  if (!course) return <div>Course not found</div>;

  const currentModule = course.modules[currentModuleIdx];
  const totalSlides = currentModule?.slides?.length || 0;
  const currentSlide = currentModule?.slides?.[currentSlideIdx];

  const handleNextSlide = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
        if (currentSlideIdx < totalSlides - 1) {
            setCurrentSlideIdx(prev => prev + 1);
            // Small delay to prevent double-clicks and allow animation start
            setTimeout(() => setIsNavigating(false), 300);
        } else {
            // End of module
            await saveModuleProgress(currentModuleIdx);
            
            if (currentModuleIdx < course.totalModules) {
                setCurrentModuleIdx(prev => prev + 1);
                setCurrentSlideIdx(0);
                setTimeout(() => setIsNavigating(false), 300);
            } else {
                setMode('quiz');
                setIsNavigating(false);
            }
        }
    } catch (e) {
        console.error("Navigation error:", e);
        setIsNavigating(false);
    }
  };

  const handlePrevSlide = () => {
    if (isNavigating) return;

    if (currentSlideIdx > 0) {
      setCurrentSlideIdx(prev => prev - 1);
    } else if (currentModuleIdx > 1) {
        // Go back to previous module
        const prevModuleIdx = currentModuleIdx - 1;
        const prevModule = course.modules[prevModuleIdx];
        if (prevModule) {
            setCurrentModuleIdx(prevModuleIdx);
            setCurrentSlideIdx(prevModule.slides.length - 1);
        }
    }
  };

  const handleRestart = () => {
    if (window.confirm("Start course from the beginning?")) {
      setInitialLoadDone(true); // Ensure we don't reload from DB
      setMode('learning');
      setCurrentModuleIdx(1);
      setCurrentSlideIdx(0);
      setQuizResult(null);
      setQuizAnswers({});
    }
  };

  const handleReviewContent = () => {
      setMode('learning');
      setCurrentModuleIdx(1);
      setCurrentSlideIdx(0);
      setQuizResult(null);
      setQuizAnswers({});
  };

  const handleDownloadCertificate = () => {
      if (!userProfile) return;
      const dateStr = new Date().toLocaleDateString('en-GB');
      const html = generateCertificateHtml(
          userProfile.displayName,
          course.title,
          userProfile.schoolName || 'Dads Matter Hub',
          dateStr
      );
      const win = window.open('', '_blank');
      if (win) {
          win.document.write(html);
          win.document.close();
      } else {
          alert("Please allow pop-ups to download the certificate.");
      }
  };

  const saveModuleProgress = async (moduleId: number) => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    
    // Use dot notation for nested updates to prevent overwriting other fields
    const updateData = {
      [`progress.${course.id}.completedModules`]: arrayUnion(moduleId),
      [`progress.${course.id}.lastAccessed`]: new Date().toISOString(),
      recentCourseId: course.id
    };

    try {
      await updateDoc(userRef, updateData);
      await refreshProfile();
    } catch (e: any) {
      // Fallback if the course progress map doesn't exist
      try {
          await setDoc(userRef, {
              progress: {
                  [course.id]: {
                      completedModules: arrayUnion(moduleId),
                      lastAccessed: new Date().toISOString()
                  }
              },
              recentCourseId: course.id
          }, { merge: true });
          await refreshProfile();
      } catch (fallbackErr) {
          console.error("Error saving progress:", fallbackErr);
      }
    }
  };

  const submitQuiz = async () => {
    let score = 0;
    const incorrectIndices: number[] = [];
    course.quiz.forEach((q, idx) => {
      if (quizAnswers[idx] === q.a) score++;
      else incorrectIndices.push(idx);
    });
    
    const passed = score >= (course.quiz.length - 1); 

    setQuizResult({ passed, score, incorrectIndices });

    if (currentUser) {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // We save the result regardless of pass/fail to enable "Retake" badges on dashboard
      const updateData: any = {
        [`progress.${course.id}.quizPassed`]: passed,
        [`progress.${course.id}.quizScore`]: score,
        [`progress.${course.id}.lastAccessed`]: new Date().toISOString(),
        recentCourseId: course.id
      };
      
      // Only set dateCompleted if they passed
      if (passed) {
          updateData[`progress.${course.id}.dateCompleted`] = new Date().toISOString();
      }
      
      try {
        await updateDoc(userRef, updateData);
        await refreshProfile();
      } catch (e: any) {
        // Fallback
        const fallbackProgress: any = {
            quizPassed: passed,
            quizScore: score,
            lastAccessed: new Date().toISOString()
        };
        if (passed) fallbackProgress.dateCompleted = new Date().toISOString();
        
        try {
            await setDoc(userRef, {
                progress: {
                    [course.id]: fallbackProgress
                },
                recentCourseId: course.id
            }, { merge: true });
            await refreshProfile();
        } catch (fallbackErr) {
            console.error("Error saving quiz result:", fallbackErr);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      {/* Rotate Device Prompt Overlay */}
      {showRotatePrompt && (
          <div 
              className="fixed inset-0 z-[150] bg-slate-900/95 flex-col items-center justify-center text-white lg:hidden portrait:flex landscape:hidden transition-opacity duration-500 animate-in fade-in"
              onClick={() => setShowRotatePrompt(false)}
          >
              <style>{`
                @keyframes rotate-phone {
                  0%, 20% { transform: rotate(0deg); }
                  40%, 60% { transform: rotate(90deg); }
                  80%, 100% { transform: rotate(0deg); }
                }
                .animate-rotate-90 {
                  animation: rotate-phone 2.5s ease-in-out infinite;
                }
              `}</style>
              <div className="w-20 h-40 border-4 border-white rounded-[2rem] flex flex-col items-center justify-between py-3 mb-8 animate-rotate-90 shadow-[0_0_30px_rgba(255,255,255,0.1)] bg-slate-800 pointer-events-none">
                  <div className="w-6 h-1 bg-white/50 rounded-full"></div>
                  <div className="w-5 h-5 border-2 border-white/50 rounded-full"></div>
              </div>
              <h2 className="text-3xl font-black font-heading tracking-tight mb-2 px-6 text-center">Rotate your phone</h2>
              <p className="text-slate-300 text-center font-medium max-w-[280px] px-6">
                  Turn your device sideways for the full-screen slide experience.
              </p>
              <button 
                  onClick={() => setShowRotatePrompt(false)} 
                  className="mt-12 px-6 py-2 rounded-full border border-white/20 text-white/50 hover:bg-white/10 hover:text-white transition-colors text-sm font-bold uppercase tracking-widest active:scale-95"
              >
                  Dismiss
              </button>
          </div>
      )}

      {/* Immersive Header - Hidden in mobile landscape to maximize space */}
      <header className="landscape:hidden lg:landscape:block bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="!p-1 md:!p-2 text-slate-400 hover:text-slate-800">
                    <span className="text-xl">✕</span>
                </Button>
                <div>
                    <h1 className="font-bold text-slate-900 flex items-center gap-1 md:gap-2 text-lg md:text-xl line-clamp-1">
                        <span className="text-2xl">{course.icon}</span> 
                        {course.title}
                    </h1>
                </div>
            </div>
            
            <div className="flex items-center gap-4 md:gap-6">
                 {mode === 'learning' && (
                    <>
                        <div className="hidden md:flex flex-col items-end w-48 ml-4 border-l border-slate-200 pl-6">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                                Module {currentModuleIdx} / {course.totalModules}
                            </span>
                            <ProgressBar percentage={((currentModuleIdx - 1) / course.totalModules) * 100} height="h-2" />
                        </div>
                    </>
                )}
                <Button variant="outline" onClick={handleRestart} className="!px-3 !py-1 text-xs hidden md:flex">Restart</Button>
            </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto p-0 md:p-10 flex flex-col justify-center">
        {mode === 'learning' && (
            <div className="w-full h-full md:h-auto">
                {currentModule && currentSlide ? (
                  <Card className="h-[100dvh] lg:h-auto lg:min-h-[60vh] portrait:h-[calc(100dvh-75px)] lg:portrait:h-auto flex flex-col shadow-2xl border-0 ring-1 ring-slate-900/5 rounded-none md:rounded-2xl relative overflow-hidden bg-black/5">
                    
                    {/* Overlay Navigation - Left (Mobile Only) */}
                    <button 
                        onClick={handlePrevSlide} 
                        disabled={(currentModuleIdx === 1 && currentSlideIdx === 0) || isNavigating}
                        className="lg:hidden absolute left-0 top-0 bottom-0 w-1/4 sm:w-24 z-30 flex items-center justify-start p-2 sm:p-6 opacity-30 hover:opacity-100 transition-opacity disabled:opacity-0 cursor-pointer"
                        aria-label="Previous Slide"
                    >
                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-black/40 text-white backdrop-blur-md rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-xl sm:text-2xl font-black">←</span>
                        </div>
                    </button>

                    {/* Overlay Navigation - Right (Mobile Only) */}
                    <button 
                        onClick={handleNextSlide} 
                        disabled={isNavigating}
                        className="lg:hidden absolute right-0 top-0 bottom-0 w-1/4 sm:w-24 z-30 flex items-center justify-end p-2 sm:p-6 opacity-30 hover:opacity-100 transition-opacity disabled:opacity-0 cursor-pointer"
                        aria-label="Next Slide"
                    >
                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-md-primary/80 text-white backdrop-blur-md rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-xl sm:text-2xl font-black">{currentSlideIdx === totalSlides - 1 && currentModuleIdx === course.totalModules ? "✓" : "→"}</span>
                        </div>
                    </button>

                    <div className="flex-1 p-0 md:p-8 lg:p-12 overflow-x-hidden flex flex-col min-h-0">
                        {/* Key is here to animate ONLY the content, not the card or buttons */}
                        <div key={`${currentModuleIdx}-${currentSlideIdx}`} className="animate-fade-in flex-1 flex flex-col w-full min-h-0">
                            {/* Header metadata conditionally rendered to save space */}
                            {(!currentModule.title.toLowerCase().startsWith('slide') || totalSlides > 1) && (
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 md:px-0 mb-2 gap-2">
                                    {!currentModule.title.toLowerCase().startsWith('slide') && (
                                        <Badge color="blue">Module {currentModuleIdx}: {currentModule.title}</Badge>
                                    )}
                                    {totalSlides > 1 && (
                                        <span className={`text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-widest ${currentModule.title.toLowerCase().startsWith('slide') ? 'ml-auto' : ''}`}>Slide {currentSlideIdx + 1}/{totalSlides}</span>
                                    )}
                                </div>
                            )}
                            
                            {!currentSlide.title.toLowerCase().startsWith('slide') && (
                                <h2 className="text-2xl md:text-4xl font-heading font-bold text-slate-900 mb-4 px-4 md:px-0 leading-tight">{currentSlide.title}</h2>
                            )}
                            
                            <div 
                                className="prose prose-slate max-w-none text-slate-600 leading-relaxed w-full flex-1 flex flex-col items-center justify-center min-h-0 prose-img:max-h-full lg:prose-img:max-h-none prose-img:h-full lg:prose-img:h-auto prose-img:w-full prose-img:object-contain lg:prose-img:object-cover prose-img:m-0 prose-img:rounded-none lg:prose-img:rounded-xl lg:prose-img:shadow-md"
                                dangerouslySetInnerHTML={{ __html: currentSlide.content }}
                            />
                        </div>
                    </div>

                    {/* Navigation Bar - Desktop Only */}
                    <div className="hidden lg:flex bg-slate-50/80 p-4 md:p-8 border-t border-slate-100 justify-between items-center rounded-b-none md:rounded-b-2xl backdrop-blur-sm sticky bottom-0">
                        <Button 
                            variant="ghost" 
                            onClick={handlePrevSlide} 
                            disabled={(currentModuleIdx === 1 && currentSlideIdx === 0) || isNavigating}
                            className="!px-2 md:!px-4 text-xs md:text-sm"
                        >
                            ← Previous
                        </Button>
                        
                        {/* Slide Dots */}
                        <div className="hidden md:flex gap-2">
                            {currentModule.slides.map((_, i) => (
                                <div key={i} className={`h-2 w-2 rounded-full transition-all duration-300 ${i === currentSlideIdx ? 'bg-indigo-600 w-6' : 'bg-slate-300'}`} />
                            ))}
                        </div>
                        
                        <Button 
                            variant="primary" 
                            onClick={handleNextSlide} 
                            disabled={isNavigating}
                            className="px-4 md:px-8 shadow-indigo-500/20 text-xs md:text-sm whitespace-nowrap"
                        >
                            {currentSlideIdx === totalSlides - 1 ? (currentModuleIdx === course.totalModules ? "Start Quiz" : "Next Module") : "Next Slide →"}
                        </Button>
                    </div>
                  </Card>
                ) : (
                  <div className="p-20 text-center text-slate-400">Loading content...</div>
                )}
            </div>
        )}

        {mode === 'quiz' && !quizResult && (
            <Card className="p-8 md:p-12 shadow-2xl animate-slide-up border-0 ring-1 ring-slate-900/5">
                <div className="text-center mb-10">
                    <span className="text-5xl mb-4 block">📝</span>
                    <h2 className="text-3xl font-heading font-bold text-slate-900">Final Assessment</h2>
                    <p className="text-slate-500 mt-2">Score 80% or higher to pass this course.</p>
                </div>

                <div className="space-y-8 max-w-2xl mx-auto">
                    {course.quiz.map((q, qIdx) => (
                        <div key={qIdx} className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                            <p className="font-bold text-lg mb-4 text-slate-800">{qIdx + 1}. {q.q}</p>
                            <div className="space-y-3" role="radiogroup" aria-labelledby={`question-${qIdx}`}>
                                {q.o.map((option, oIdx) => (
                                    <label 
                                        key={oIdx} 
                                        tabIndex={0}
                                        role="radio"
                                        aria-checked={quizAnswers[qIdx] === oIdx}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setQuizAnswers(prev => ({...prev, [qIdx]: oIdx}));
                                            }
                                        }}
                                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/50 ${quizAnswers[qIdx] === oIdx ? 'border-indigo-600 bg-indigo-50/50' : 'border-transparent bg-white hover:border-slate-200 shadow-sm'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-colors ${quizAnswers[qIdx] === oIdx ? 'border-indigo-600' : 'border-slate-300 group-hover:border-indigo-400'}`}>
                                            {quizAnswers[qIdx] === oIdx && <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />}
                                        </div>
                                        <input 
                                            type="radio" 
                                            name={`q-${qIdx}`} 
                                            value={oIdx} 
                                            checked={quizAnswers[qIdx] === oIdx}
                                            onChange={() => setQuizAnswers(prev => ({...prev, [qIdx]: oIdx}))}
                                            className="hidden"
                                            tabIndex={-1}
                                        />
                                        <span className={`text-sm md:text-base ${quizAnswers[qIdx] === oIdx ? 'text-indigo-900 font-medium' : 'text-slate-600'}`}>{option}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-10 text-center">
                    <Button onClick={submitQuiz} className="w-full md:w-auto px-12 py-4 text-lg shadow-xl shadow-indigo-500/30">Submit Answers</Button>
                </div>
            </Card>
        )}

        {mode === 'quiz' && quizResult && (
             <div className="max-w-xl mx-auto w-full animate-slide-up">
                 <Card className={`p-10 text-center shadow-2xl border-t-8 ${quizResult.passed ? 'border-emerald-500' : 'border-rose-500'}`}>
                     <div className="text-8xl mb-6 animate-bounce">{quizResult.passed ? '🎉' : '🤔'}</div>
                     
                     <h2 className="text-3xl font-heading font-bold text-slate-900 mb-2">
                         {quizResult.passed ? 'Assessment Passed!' : 'Assessment Failed'}
                     </h2>
                     
                     <div className="my-6">
                        <span className={`text-5xl font-bold ${quizResult.passed ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {Math.round((quizResult.score / course.quiz.length) * 100)}%
                        </span>
                        <p className="text-slate-400 mt-2 font-medium">You scored {quizResult.score} out of {course.quiz.length}</p>
                     </div>

                     <div className="flex flex-col gap-3 mt-8">
                         {quizResult.passed && (
                             <Button onClick={handleDownloadCertificate} className="w-full !bg-emerald-600 hover:!bg-emerald-700 shadow-emerald-500/30">
                                 Download Certificate
                             </Button>
                         )}

                         <Button onClick={() => navigate('/dashboard')} variant="ghost" className="w-full">Back to Dashboard</Button>
                         
                         {!quizResult.passed && (
                            <>
                                {quizResult.incorrectIndices.length > 0 && (
                                    <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-left my-2 animate-fade-in max-h-60 overflow-y-auto custom-scrollbar shadow-inner">
                                        <p className="text-sm font-bold text-rose-800 mb-2">You answered the following questions incorrectly:</p>
                                        <ul className="list-disc pl-5 text-sm text-rose-700 space-y-2">
                                            {quizResult.incorrectIndices.map(idx => (
                                                <li key={idx} className="leading-relaxed">
                                                    <span className="font-bold mr-1">Question {idx + 1}:</span>
                                                    {course.quiz[idx].q}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3 mt-2">
                                    <Button variant="outline" onClick={handleReviewContent}>Review Content</Button>
                                    <Button variant="secondary" onClick={() => { setQuizResult(null); setQuizAnswers({}); }}>Try Again</Button>
                                </div>
                            </>
                         )}
                     </div>
                 </Card>
             </div>
        )}

      </main>
    </div>
  );
};

export default CoursePlayer;
